import { arrayExtensions, stringExtensions } from "@architecture-innovation-transformation/lib-common";
import { Button, Col, Collapse, Create, DatePicker, Divider, Form, Icons, Input, InputNumber, Radio, Result, Row, Select, Spin, Tag, message, useForm, useSelect } from "@pankod/refine-antd";
import { useCreateMany, useGetIdentity, useNavigation, useOne } from "@pankod/refine-core";
import { StateButtons } from "components/common";
import { ShowUserCard } from "components/profile/showUserCard";
import { CancelButton } from "components/utils/cancelButton";
import dayjs from "dayjs";
import { IBaseProps, IListData, ILocations, ILookup, IStateTransitionComments, IUser } from "interfaces";
import { IDemand, IResourcingRequest, IResourcingRequestMetadata } from "interfaces/resourcing";
import { useEffect, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, RESOURCE_PATH, SELECTION_LISTS, buildAttachmentUrl, generateGroupFetchID, getGUID, validateRGSIDs } from "scripts/site";

export const RequestForm: React.FC<{ dataProvider: string }> = ({ dataProvider }) => {
    const { data: userData } = useGetIdentity();
    const userObj = userData as IUser;
    const { list } = useNavigation();
    const { Panel } = Collapse;
    const { mutate: createPositions } = useCreateMany<IDemand>();

    const { form, formProps, queryResult, onFinish, redirect } = useForm<IResourcingRequest>({
        dataProviderName: dataProvider,
        mutationMode: "pessimistic"
    });

    const { data: metadata, isLoading: metadataLoading } = useOne<IResourcingRequestMetadata>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.METADATA,
        id: RESOURCE_PATH.RESOURCINGREQUEST,
        queryOptions: {
            enabled: true,
            staleTime: 300000
        }
    });

    const { selectProps: countryProps } = useSelect<ILookup>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.COUNTRY,
        optionLabel: "name",
        optionValue: "id",
        sort: [
            {
                field: "name",
                order: "asc"
            }
        ],
        fetchSize: 100
    });

    const { queryResult: locationsData } = useSelect<ILocations>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LOCATION,
        sort: [
            {
                field: "name",
                order: "asc"
            },
        ],
        fetchSize: 100
    });

    const { selectProps: userProps } = useSelect<ILookup>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.PEOPLE,
        optionLabel: "nameEmail",
        optionValue: "id",
        sort: [
            {
                field: "lastAccessedAt",
                order: "desc"
            },
        ],
        fetchSize: 50
    });

    const { data: skillsList } = useOne<IListData>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LIST,
        id: SELECTION_LISTS.TECHNICAL_EXPERTISE
    });

    const { data: rolesList } = useOne<IListData>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LIST,
        id: SELECTION_LISTS.RESOURCING_ROLES
    });

    const metaConfig = metadata?.data.config;
    if (queryResult?.data?.data?.billingStartDate) {
        queryResult.data.data.billingStartDate = dayjs(queryResult.data.data.billingStartDate);
    }

    if (queryResult?.data?.data?.requestReceivedDate) {
        queryResult.data.data.requestReceivedDate = dayjs(queryResult.data.data.requestReceivedDate);
    }

    const record = queryResult?.data?.data;
    const locations = locationsData?.data?.data;
    const [canCreate, switchCanCreate] = useState(false);
    const [canUpdate, switchCanUpdate] = useState(false);
    const [positionType, setPositionType] = useState("");
    const [resourcingAdmin, switchResourcingAdmin] = useState(false);
    const [requestState, switchRequestState] = useState("draft");
    const [activePanel, setActivePanel] = useState<string[]>(['basic']);
    const [posCount, setPositionCount] = useState<number>(1);
    const threeColumnLayout = { xs: { span: 24 }, sm: { span: 24 }, md: { span: 8 }, lg: { span: 8 }, xl: { span: 8 }, xxl: { span: 8 } };
    const twoColumnLaytout = { xs: { span: 24 }, sm: { span: 24 }, md: { span: 16 }, lg: { span: 16 }, xl: { span: 16 }, xxl: { span: 16 } };
    const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32 };

    useEffect(() => {
        switchCanCreate((metadata?.data.config.roleIdsCanCreate ?? []).some(r => userObj?.roleIds.some(ri => r === ri)));
        switchCanUpdate((record?.stateManager.assignedTo ?? []).some(x => x === userObj?.id));
        switchResourcingAdmin((["resourcingAdmin", "resourcingTeamMember"]).some(r => userObj?.entityRoleIds.some(ri => r === ri)));
        if (record?.stateManager) {
            switchRequestState(record?.stateManager.state);
        }
        if (record?.positionType) {
            setPositionType(record?.positionType);
        }
    }, [metadata?.data.config.roleIdsCanCreate, record?.stateManager, record?.positionType, userObj?.id, userObj?.roleIds, userObj?.entityRoleIds])

    useEffect(() => {
        if (record?.positionCount) {
            setPositionCount(+record.positionCount)
        }
    }, [record?.positionCount]);

    const [validateForm, setValidateForm] = useState<boolean>(true);
    const [disableForm, setDisableForm] = useState<boolean>(false);

    const { data: onsiteDPProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryPartnerOnsiteGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryPartnerOnsiteGroup
        }
    });

    const { data: offshoreDPProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryPartnerOffshoreGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryPartnerOffshoreGroup
        }
    });

    const { data: deliveryHeadProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryHeadGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryHeadGroup
        }
    });

    const { data: salesExecutiveProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.salesExecutiveGroup),
        queryOptions: {
            enabled: !!metaConfig?.salesExecutiveGroup
        }
    });

    const submitWithComments = (values: IStateTransitionComments, stateAction: string) => {
        if (!form.getFieldValue('id')) { form.setFieldsValue({ 'id': getGUID() }) }
        if (!form.getFieldValue('requestor')) { form.setFieldsValue({ 'requestor': userObj?.id }) }

        // Add the State transition for Form Submission in case of submit
        form.setFieldsValue({
            'stateAction': stateAction,
            'requestReceivedDate': dayjs().toISOString(),
            'comments': values.comments
        })
        form.submit();
    }

    const setFormPreValidationRules = (stateAction: string) => {
        setValidateForm(!stringExtensions.stringEquals(stateAction, "revert"));
        setDisableForm(true);
    }

    const onFailureFormValidation = () => {
        setActivePanel(["basic", "aboutteam", "skills", "stfftm"]);
        setDisableForm(false);
    }

    const handleFormSave = () => {
        setDisableForm(true);
        setValidateForm(false);
        form.validateFields().then(async () => {
            let initialGUID = undefined;
            if (!form.getFieldValue('id')) {
                initialGUID = getGUID();
                form.setFieldsValue({ 'id': initialGUID });
            }
            if (!form.getFieldValue('requestor')) { form.setFieldsValue({ 'requestor': userObj?.id }) }

            if (initialGUID) {
                const response = await onFinish(form.getFieldsValue());
                redirect("edit", response?.data?.id || form.getFieldValue('id'));
            } else {
                form.submit();
            }
        })
            .catch((errorInfo) => {
                setActivePanel(["basic", "aboutteam", "skills", "stfftm"]);
                setDisableForm(false);
            });
    }

    const publishWithComments = (values: IStateTransitionComments, stateAction: string) => {
        // Two state actions possible while entry is in Submitted state
        // First is Publish
        if (stateAction === "publish") {
            let reqId = form.getFieldValue('requestID');
            let rgdIdValues = form.getFieldValue('rgsIds');

            if (record && reqId && arrayExtensions.validateArray(rgdIdValues)) {
                let positions: any[] = [];

                //Disable the Form
                setDisableForm(true);

                // Create Positions after Form Validation
                message.info(`Creating ${posCount} position(s) for request: ${record.id}`);
                let epochTime = new Date().getTime();

                for (let index = 0; index < posCount; index++) {
                    // Formulate the base properties for the Position
                    let dem: { [key: string]: any } = {
                        id: `${reqId}-${epochTime}-${String(index + 1).padStart(3, "0")}`,
                        resourcingRequestID: record.id || form.getFieldValue('id'),
                        rgsId: +rgdIdValues[index] || 0,
                        empIDSelectedCandidate: "",
                        nameSelectedCandidate: "",
                        backgroundScreeningID: "",
                        closureChannel: null,
                        notes: "",
                        closureDate: "",
                        mappedDate: ""
                    }

                    // Copy the form values and merge it with record
                    let mergedRequest = { ...record, ...form.getFieldsValue() };
                    let fieldsNotToCopy = metaConfig?.skipFieldsCopyToPosition || [];
                    // Copy the values from resourcingrequest down to position
                    for (const [key, value] of Object.entries(mergedRequest)) {
                        if (!fieldsNotToCopy.includes(key)) {
                            dem[key] = value;
                        }
                    }

                    // Position count will always be 1 in all positions
                    dem["positionCount"] = 1;

                    positions.push(dem);
                }

                createPositions(
                    {
                        resource: RESOURCE_PATH.POSITION,
                        dataProviderName: DATAPROVIDER_CREATE,
                        values: positions
                    },
                    {
                        onError: () => {
                            message.error(`Unable to create ${posCount} position(s) for request: ${record.id}`);
                            setDisableForm(true);
                        },
                        onSuccess: () => {
                            // Prepeare the form for State Change
                            form.setFieldsValue({
                                'stateAction': stateAction,
                                'positions': positions.map((pos) => { return { "id": pos.id, "currentState": "open", "rgsId": pos.rgsId } }),
                                'comments': values.comments
                            })
                            // Submit the form
                            form.submit();
                        }
                    }
                )
            } else {
                // Unable to read the reqId
                message.error(`Request ID/RGS IDs is/are not found, please provide Request ID/RGS Ids and then try to publish. Please note as per business rule, these fields are mandatory to create position(s).`);
                setDisableForm(false);
            }
        }
        // Second is "Revert Back"
        else if (stateAction === "revert") {
            // Prepeare the form for State Change
            form.setFieldsValue({
                'stateAction': stateAction,
                'comments': values.comments
            })
            // Submit the form
            form.submit();
        }
    }

    const changeActivePanel = (key: string | string[]) => {
        if (typeof key === "string") {
            key = [key];
        }
        setActivePanel(key);
    };

    const handleSelectChange = (value: string | { value: string, label: string }, fieldName: string) => {
        if (value === undefined) {
            form.setFieldsValue({ [fieldName]: null })
        }
    };

    const metaFields = metadata?.data.fields;
    const crmGuidanceUrl = buildAttachmentUrl(RESOURCE_PATH.METADATA, RESOURCE_PATH.RESOURCINGREQUEST, metaFields?.find(f => f.name === "crmGuidance")?.attachment);

    return (
        (metadataLoading) ?
            <Result
                status="info"
                title="Checking permission !!!"
                subTitle="Kindly wait for few moments while we verify your permission and load this request for you."
            />
            :
            (canCreate && requestState === "draft") || (canUpdate && requestState === "draft") || (resourcingAdmin && requestState === "submitted") ?
                <Spin spinning={disableForm || metadataLoading || locationsData.isLoading}>
                    <Create
                        title="Resourcing Request"
                        footerButtons={() => (
                            <>
                                {record &&
                                    <StateButtons {...record as IBaseProps}
                                        resourceToAct={RESOURCE_PATH.RESOURCINGREQUEST}
                                        resourceToActId={record?.id || "0"}
                                        resourceToNavigate={RESOURCE_PATH.RESOURCINGREQUEST}
                                        hideActions={['reassign']}
                                        skipAssignedToCheck={true}
                                        form={form}
                                        setFormPreValidationRules={setFormPreValidationRules}
                                        onFailureFormValidate={onFailureFormValidation}
                                        customMutate={requestState === "submitted" ? publishWithComments : submitWithComments}
                                    />
                                }
                                {requestState === "draft" && record?.id === undefined &&
                                    <Button
                                        type="default"
                                        disabled={true}
                                    >
                                        <Icons.FileDoneOutlined />
                                        Submit
                                    </Button>
                                }
                                <Button
                                    type="default"
                                    onClick={() => handleFormSave()}
                                >
                                    <Icons.SaveOutlined />
                                    Save Progress
                                </Button>
                                <CancelButton />
                            </>
                        )}
                    >
                        <Form {...formProps} form={form} layout="vertical" autoComplete="off" scrollToFirstError={true}>
                            <Collapse activeKey={activePanel} expandIconPosition="end" onChange={changeActivePanel}>
                                <Panel header={<b>Basic Details</b>} key="basic">
                                    <Form.Item
                                        label="Request ID"
                                        name="id"
                                        hidden={true}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        name="stateAction"
                                        hidden={true}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        name="comments"
                                        hidden={true}
                                    >
                                        <Input />
                                    </Form.Item>
                                    {requestState === "draft" &&
                                        <>
                                            <Form.Item
                                                name="requestReceivedDate"
                                                hidden={true}
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                label="Requestor"
                                                name="requestor"
                                                hidden={true}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </>
                                    }
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Project Name"
                                                name="projectName"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        max: 100
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Please provide project name" />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Role Name"
                                                name="name"
                                                tooltip="Select role from available list"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                    }
                                                ]}
                                            >
                                                <Select
                                                    placeholder="Select role"
                                                    showSearch
                                                >
                                                    {rolesList?.data.data.map((m, i) => (
                                                        <Select.Option value={m} key={i}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Title"
                                                name="title"
                                                tooltip="Enter a title for this role if different from 'Role Name'. Default to 'Role Name' if not different."
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        max: 100
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Please provide title" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Position Type"
                                                name="positionType"
                                                tooltip={<>
                                                    Select the appropriate position type.
                                                    <br />
                                                    Select "New" only if you want to Hire a new resource.
                                                    <br />
                                                    Select "Replacement" for replacement of an existing resource.
                                                </>
                                                }
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Select
                                                    placeholder="Select Position Type"
                                                    onChange={(value) => {
                                                        setPositionType(value);
                                                        if (value === "Investment") {
                                                            form.setFieldsValue({ 'billable': "Non-Billable" });
                                                        } else {
                                                            form.resetFields(['billable']);
                                                        }

                                                        if (value === "Replacement") {
                                                            form.setFieldsValue({ 'positionCount': 1 });
                                                            setPositionCount(1);
                                                        } else {
                                                            form.resetFields(['positionCount']);
                                                        }

                                                        if (value === "New-Committed" || value === "Replacement") {
                                                            form.setFieldsValue({ 'confidence': "Confirmed" });
                                                        } else {
                                                            form.resetFields(['confidence']);
                                                        }
                                                    }}
                                                >
                                                    {(metaConfig?.positionType ?? []).map((m, i) => (
                                                        <Select.Option value={m} key={i}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="CRM/Opportunity ID"
                                                name="crmOpportunityId"
                                                tooltip={<>
                                                    CRM ID for this request. Staffing should have access to the CRM ID.
                                                    <br />
                                                    {crmGuidanceUrl &&
                                                        <div>
                                                            <a href={crmGuidanceUrl} rel="noreferrer" target={"_blank"}>View guidance</a>
                                                        </div>
                                                    }
                                                </>
                                                }
                                                dependencies={["positionType"]}
                                                rules={[
                                                    {
                                                        required: (positionType !== "Replacement" && validateForm),
                                                        message: "CRM/Opportunity ID is required.",
                                                    },
                                                    {
                                                        whitespace: true,
                                                        type: "integer",
                                                        message: "CRM/Opportunity ID should be 7 digits long numeric value starting with 1 or 2",
                                                        validator: async (_, value) => {
                                                            if (value || (positionType !== "Replacement" && validateForm)) {
                                                                let strValue = value?.toString();
                                                                if (strValue && strValue.length === 7 && (strValue.startsWith("1") || strValue.startsWith("2"))) {
                                                                    return Promise.resolve();
                                                                }
                                                                else {
                                                                    return Promise.reject(new Error("CRM/Opportunity ID does not match the criteria."));
                                                                }
                                                            }
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber min={1} precision={0} maxLength={7} placeholder="CRM/Opportunity ID" style={{ width: "100%" }} controls={false} />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="WON"
                                                name="projectWON"
                                                tooltip="WON should be 7 or 8 digits long numeric value starting with 1 or 2"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "integer",
                                                        message: "Project WON should be 7 or 8 digits long numeric value starting with 1 or 2",
                                                        validator: async (_, value) => {
                                                            if (validateForm) {
                                                                let strValue = value.toString();
                                                                if (strValue && strValue.length > 6 && strValue.length < 9 && (strValue.startsWith("1") || strValue.startsWith("2"))) {
                                                                    Promise.resolve();
                                                                }
                                                                else {
                                                                    throw new Error("WON does not match the criteria.")
                                                                }
                                                            }
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber min={0} precision={0} maxLength={8} placeholder="WON" style={{ width: 200 }} controls={false} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {positionType === "Replacement" &&
                                        <>
                                            <Divider orientation="left">Additional details for Replacement position</Divider>
                                            <Row gutter={rowGutter}>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Replacement for Employee"
                                                        name="replacementForEmployeeName"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Input placeholder="Provide name of employee being replaced" />
                                                    </Form.Item>

                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Replacement for Employee ID"
                                                        name="replacementForEmployeeId"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                type: "number",
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} placeholder="Provide employee ID of employee being replaced" controls={false} />
                                                    </Form.Item>
                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Replacement reason"
                                                        name="replacementReason"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Select placeholder="Select reason for replacement" showSearch>
                                                            {metaConfig?.replacementReason.sort().map((m, i) => (
                                                                <Select.Option value={m} key={`rr-${i}`}>{m}</Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Divider />
                                        </>
                                    }
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Billable/Non Billable"
                                                name="billable"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Radio.Group
                                                    disabled={positionType === "Investment"}
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                    name="billable"
                                                    options={(["Billable", "Non-Billable"]).map(a => a)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Confidence"
                                                name="confidence"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Radio.Group
                                                    optionType="button"
                                                    disabled={positionType === "New-Committed" || positionType === "Replacement"}
                                                    buttonStyle="solid"
                                                    name="confidence"
                                                    options={(["Confirmed", "Tentative"]).map(a => a)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Number of positions"
                                                name="positionCount"
                                                tooltip="Specify the number of resources required with the skills specified in this request. For different skills/location, please create a new request."
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        type: "integer"
                                                    }
                                                ]}
                                            >
                                                <InputNumber min={1} max={15} precision={0} disabled={positionType === "Replacement"} placeholder="Position count" style={{ width: 200 }}
                                                    onChange={(value) => {
                                                        setPositionCount(value || 1);
                                                    }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prevValues, currentValues) => prevValues.billable !== currentValues.billable}
                                    >
                                        {({ getFieldValue }) =>
                                            getFieldValue('billable') === "Billable" ? (
                                                <>
                                                    <Divider orientation="left">Additional details for billable resources</Divider>
                                                    <Row gutter={rowGutter}>
                                                        <Col className="gutter-row" {...threeColumnLayout}>
                                                            <Form.Item
                                                                label="Billing Type"
                                                                name="billingType"
                                                                rules={[
                                                                    {
                                                                        required: validateForm,
                                                                        whitespace: true
                                                                    }
                                                                ]}
                                                            >
                                                                <Radio.Group
                                                                    optionType="button"
                                                                    buttonStyle="solid"
                                                                    name="billingType"
                                                                    options={(metaConfig?.billingType ?? []).map(a => a)}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col className="gutter-row" {...threeColumnLayout}>
                                                            <Form.Item
                                                                label="Billing Start Date"
                                                                name="billingStartDate"
                                                                tooltip="Provide a future date"
                                                                rules={[
                                                                    {
                                                                        required: validateForm,
                                                                        whitespace: true,
                                                                        type: "date",
                                                                        validator: async (_, value) => {
                                                                            if (validateForm || value) {
                                                                                if (value > dayjs().endOf('day') && value < dayjs().add(6, 'months').endOf('day')) {
                                                                                    return Promise.resolve();
                                                                                }
                                                                                else {
                                                                                    throw new Error("Billing start date should be a future date and within 6 months from today")
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                ]}
                                                            >
                                                                <DatePicker picker="date"
                                                                    style={{ width: 280 }}
                                                                    placeholder="YYYY-MM-DD"
                                                                    format={"YYYY-MM-DD"}
                                                                    disabledDate={(selDate) => selDate && (selDate < dayjs().endOf('day') || selDate > dayjs().add(6, 'months').endOf('day'))}
                                                                    allowClear={false}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col className="gutter-row" {...threeColumnLayout}>
                                                            <Form.Item
                                                                label="Bill Rate (USD per Hour)"
                                                                name="billRate"
                                                                rules={[
                                                                    {
                                                                        required: validateForm,
                                                                        type: "integer"
                                                                    }
                                                                ]}
                                                            >
                                                                <InputNumber min={0} max={200} precision={0} placeholder="0" addonAfter="$" style={{ width: 200 }} />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : null
                                        }
                                    </Form.Item>
                                    <Divider orientation="left">Constraints</Divider>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Work Location Constraints"
                                                name="locationConstraint"
                                                tooltip="Where are the associates expected to work from?"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Radio.Group
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                    name="locationConstraint"
                                                    options={(["Remote", "MS Campus", "TCS Campus"]).map(a => a)}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...twoColumnLaytout}>
                                            <Form.Item
                                                label="Resourcing Constraints"
                                                name="resourceConstraint"
                                                initialValue="None"
                                                rules={[
                                                    {
                                                        required: false,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Radio.Group
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                    name="resourceConstraint"
                                                    options={(["BA Only", "FTE Only", "Citizen Only", "Trainee Only", "None"]).map(a => a)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header={<b>Hiring Team</b>} forceRender key="aboutteam">
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Country"
                                                name="country"
                                                tooltip="Where is the project executed from?"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        type: "string",
                                                    }
                                                ]}
                                            >
                                                <Select placeholder="Select Country"
                                                    onChange={(_value) => {
                                                        form.setFieldsValue({ 'location': null });
                                                    }}
                                                    showSearch
                                                    {...countryProps}
                                                >
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prevValues, currentValues) => prevValues.country !== currentValues.country}
                                            >
                                                {({ getFieldValue }) =>
                                                    <Form.Item
                                                        label="Location"
                                                        name="location"
                                                        tooltip="Where is the project executed from?"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                type: "string"
                                                            }
                                                        ]}
                                                    >
                                                        <Select placeholder={form.getFieldValue('country') ? "Select Location" : "Select a Country first"} showSearch>
                                                            {locations?.filter(loc => loc.countryId === getFieldValue("country")).map((m, i) => (
                                                                <Select.Option value={m.id} key={`lc-${i}`}>{m.name}</Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                }
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Project Duration (In Months)"
                                                name="projectDuration"
                                                rules={[
                                                    {
                                                        required: true,
                                                        type: "integer"
                                                    }
                                                ]}
                                            >
                                                <InputNumber min={1} max={420} precision={0} placeholder="Project Duration" style={{ width: 200 }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Group"
                                                name="group"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "string"
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Select business group" showSearch>
                                                    {metaConfig?.group.sort().map((m, i) => (
                                                        <Select.Option value={m} key={`grp-${i}`}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Sub Group"
                                                name="subGroup"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        max: 100
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Provide business sub group (Ex: E360)" />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Customer Interview"
                                                name="customerInterview"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Radio.Group
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                    name="customerInterview"
                                                    options={(["Yes", "No"]).map(a => a)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Hiring Manager"
                                                name="hiringManager"
                                                tooltip="To look up any missing name type the name of the person and then select"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "email"
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Search Name or Email" {...userProps} allowClear
                                                    onChange={(e) => { handleSelectChange(e, "hiringManager") }} />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Sales Executive (BRM)"
                                                name="salesExecutive"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "email"
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    placeholder="Select Sales Executive (BRM)"
                                                    allowClear
                                                    showSearch
                                                    onChange={(e) => { handleSelectChange(e, "salesExecutive") }}>
                                                    {arrayExtensions.sortByName(salesExecutiveProps?.data || []).map((m, i) => (
                                                        <Select.Option value={m.id} key={`se-${i}`}>{m.name}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="MS Manager"
                                                name="msManager"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        max: 100
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="Name and alias of MS Manager" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Offshore Delivery Partner (L2)"
                                                name="deliveryPartnerOffshore"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "email",
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Select Offshore Delivery Partner (L2)" allowClear showSearch
                                                    onChange={(e) => { handleSelectChange(e, "deliveryPartnerOffshore") }}>
                                                    {arrayExtensions.sortByName(offshoreDPProps?.data || []).map((m, i) => (
                                                        <Select.Option value={m.id} key={`dpof-${i}`}>{m.name}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Onsite Delivery Partner (L2)"
                                                name="deliveryPartnerOnsite"
                                                rules={[
                                                    {
                                                        required: false,
                                                        whitespace: true,
                                                        type: "email",
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Select Onsite Delivery Partner (L2)" allowClear showSearch
                                                    onChange={(e) => { handleSelectChange(e, "deliveryPartnerOnsite") }}>
                                                    {arrayExtensions.sortByName(onsiteDPProps?.data || []).map((m, i) => (
                                                        <Select.Option value={m.id} key={`dpo-${i}`}>{m.name}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Delivery Head (L1)"
                                                name="deliveryHead"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "email",
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    placeholder="Select Delivery Head (L1)"
                                                    allowClear
                                                    showSearch
                                                    onChange={(e) => { handleSelectChange(e, "deliveryHead") }} >
                                                    {arrayExtensions.sortByName(deliveryHeadProps?.data || []).map((m, i) => (
                                                        <Select.Option value={m.id} key={`dh-${i}`}>{m.name}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="TR Interviewers"
                                                name="trInterviewers"
                                                tooltip="To look up any missing name type the name of the person and then select"
                                                rules={[
                                                    {
                                                        required: false,
                                                        whitespace: true,
                                                        type: "array"
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Search Name or Email" mode="multiple" {...userProps} allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="MR Interviewers"
                                                name="mrInterviewers"
                                                tooltip="To look up any missing name type the name of the person and then select"
                                                rules={[
                                                    {
                                                        required: false,
                                                        whitespace: true,
                                                        type: "array"
                                                    },
                                                ]}
                                            >
                                                <Select placeholder="Search Name or Email" mode="multiple" {...userProps} allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            {requestState === "draft" &&
                                                <>
                                                    <Form.Item
                                                        label="Requestor"
                                                    >
                                                        <ShowUserCard id={record?.requestor || userObj?.id} ></ShowUserCard>
                                                    </Form.Item>
                                                </>
                                            }
                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header={<b>Skills</b>} forceRender key="skills">
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Skill Category"
                                                name="skillCategory"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true
                                                    }
                                                ]}
                                            >
                                                <Select placeholder="Select skill category from list" showSearch>
                                                    {metaConfig?.skillCategory.sort().map((m, i) => (
                                                        <Select.Option value={m} key={`rr-${i}`}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Required Skills"
                                                name="requiredSkills"
                                                tooltip="Select or add skills. Skill should primarily be a combination of alphabets, numbers and supported special characters which include /_-.&#:+() and space character "
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        type: "array"
                                                    },
                                                    {
                                                        pattern: new RegExp(/^[A-Za-z0-9 /_,-.&#:+()]+$/),
                                                        message: "Invalid character found. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                    }
                                                ]}
                                            >
                                                <Select mode="tags"
                                                    placeholder="Type a required skill and press enter. You can add as many as you want."
                                                    tokenSeparators={[',']}
                                                >
                                                    {skillsList?.data.data.map((m, i) => (
                                                        <Select.Option value={m} key={i}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Good to have skills"
                                                name="goodToHaveSkills"
                                                tooltip="Select or add skills. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        type: "array"
                                                    },
                                                    {
                                                        pattern: new RegExp(/^[A-Za-z0-9 /_,-.&#:+()]+$/),
                                                        message: "Invalid character found. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                    }
                                                ]}
                                            >
                                                <Select mode="tags"
                                                    placeholder="Type a skill and press enter. You can add as many as you want."
                                                    tokenSeparators={[',']}
                                                >
                                                    {skillsList?.data.data.map((m, i) => (
                                                        <Select.Option value={m} key={i}>{m}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={rowGutter}>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Minimum experience required (In Years)"
                                                name="experienceRequired"
                                                tooltip="Please enter min experience required as whole number between 0 to 15"
                                                rules={[
                                                    {
                                                        required: validateForm,
                                                        whitespace: true,
                                                        type: "integer"
                                                    }
                                                ]}
                                            >
                                                <InputNumber min={0} max={15} precision={0} style={{ width: 200 }} />
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                            <Form.Item
                                                label="Keywords"
                                                name="keywords"
                                                rules={[
                                                    {
                                                        required: false,
                                                        type: "array"
                                                    }
                                                ]}
                                            >
                                                <Select mode="tags"
                                                    placeholder="Type a keyword and press enter. You can add as many as you want."
                                                    tokenSeparators={[',']}
                                                >
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col className="gutter-row" {...threeColumnLayout}>
                                        </Col>
                                    </Row>
                                    <Form.Item
                                        label="Responsibilities"
                                        name="responsibilities"
                                        rules={[
                                            {
                                                required: validateForm,
                                                whitespace: true,
                                                max: 10000
                                            },
                                        ]}
                                    >
                                        <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder="Provide detailed description of responsibilities" />
                                    </Form.Item>
                                </Panel>
                                {resourcingAdmin && requestState === "submitted" &&
                                    <Panel header={<b>Staffing Section</b>} forceRender key="stfftm">
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Requestor"
                                                    name="requestor"
                                                    tooltip="To look up any missing name type the name of the person and then select"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "email"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Search Name or Email" {...userProps} allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Delivery Unit (DU)"
                                                    name="deliveryUnit"
                                                    initialValue="ADM"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Select DU" showSearch>
                                                        {metaConfig?.deliveryUnit.sort().map((m, i) => (
                                                            <Select.Option value={m} key={`du-${i}`}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Request ID"
                                                    name="requestID"
                                                    validateTrigger={["onBlur", "onChange"]}
                                                    tooltip="Request ID should be 7 digits long numeric value starting with 3 or 4."
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            type: "integer",
                                                            whitespace: true,
                                                            validator: async (_, value) => {
                                                                if (validateForm || value) {
                                                                    let strValue = value?.toString();
                                                                    if (strValue && strValue.length === 7 && (strValue.startsWith("3") || strValue.startsWith("4"))) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    else {
                                                                        throw new Error("Request ID should be 7 digits long numeric value starting with 3 or 4.")
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={0} precision={0} placeholder="Please provide Request ID" style={{ width: 200 }} controls={false} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    name="rgsIds"
                                                    label="RGS IDs"
                                                    tooltip={
                                                        <>
                                                            RGS ID needs to be a positive number. RGS IDs can be provided in two way:
                                                            <br />
                                                            <ol>
                                                                <li><i>Paste 'SPACE' separated list directly into the provided input box</i></li>
                                                                <li><i>Type and press Enter/Space bar for providing one RGS ID at a time</i></li>
                                                            </ol>
                                                        </>
                                                    }
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            type: "array",
                                                            len: posCount,
                                                            message: `Please enter ${posCount} valid RGS ID(s). RGS ID should be 7 digits long numeric value starting with 8 or 9`,
                                                            validator: async (_, value) => {
                                                                if (validateForm || value) {
                                                                    if (arrayExtensions.validateArray(value) && value.length === posCount && validateRGSIDs(value)) {
                                                                        Promise.resolve();
                                                                    }
                                                                    else {
                                                                        throw new Error("Provide Valid RGS IDs");
                                                                    }
                                                                }
                                                            }
                                                        }]}
                                                >
                                                    <Select mode="tags"
                                                        placeholder="Enter 'SPACE' seperated RGS IDs"
                                                        open={false}
                                                        tokenSeparators={[' ']}
                                                    >
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Request Received Date"
                                                    name="requestReceivedDate"
                                                >
                                                    {record &&
                                                        <Tag>{dayjs(record.requestReceivedDate).format("LL")}</Tag>
                                                    }
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    name="positions"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Panel>
                                }
                            </Collapse>
                            <br />
                            <Form.Item
                                label="Remarks"
                                name="remarks"
                                tooltip="Mention specific candidates identified, RGS auto-generated for replacement requests, Country, City or any other master data additions related to this request here."
                                rules={[
                                    {
                                        required: false,
                                        whitespace: true,
                                        max: 10000
                                    },
                                ]}
                            >
                                <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder="Provide additional details about the request, if any." />
                            </Form.Item>
                        </Form>
                    </Create>
                </Spin>
                :
                <Result
                    status="403"
                    title="Edit operation not allowed."
                    subTitle="Sorry, In current state, you don't have permission to edit this request. Kindly contact Requestor or Resourcing Team for any changes needed."
                    extra={<Button type="primary" onClick={() => list(RESOURCE_PATH.RESOURCINGREQUEST)}>Back to Resourcing Requests</Button>}
                />
    );
};